import { createTrackerForCategory } from '@gonfalon/analytics/createTrackerForCategory';
import type { AnalyticEventData } from '@gonfalon/analytics/types';

import type { NavMode } from './Navigation/NavModeSwitcher';

export const trackEnvironmentEvent = (event: string, attributes?: AnalyticEventData) =>
  createTrackerForCategory('Environment')(event, attributes);

const trackLeftNav = createTrackerForCategory('Left Nav');

type LeftNavVariant = 'new' | 'legacy';

export const trackLeftNavItemClicked = (attributes: {
  key: string;
  label: string;
  section: string;
  nav: LeftNavVariant;
  mode?: NavMode;
}) => trackLeftNav('Item Clicked', attributes);

export const trackedItemClick =
  (item: { key: string; label: string; onClick?: () => void }, section: string, nav: LeftNavVariant, mode?: NavMode) =>
  () => {
    trackLeftNavItemClicked({ key: item.key, label: item.label, section, nav, mode });
    item.onClick?.();
  };

export const trackLeftNavSectionClicked = (attributes: {
  label: string;
  nav: LeftNavVariant;
  mode: 'expanded' | 'collapsed';
  navMode?: NavMode;
}) => trackLeftNav('Section Clicked', attributes);

export const trackLeftNavSectionExpanded = (attributes: { label: string; nav: LeftNavVariant; mode?: NavMode }) =>
  trackLeftNav('Section Expanded', attributes);

export const trackLeftNavSectionCollapsed = (attributes: { label: string; nav: LeftNavVariant; mode?: NavMode }) =>
  trackLeftNav('Section Collapsed', attributes);

const trackNavMode = createTrackerForCategory('Nav Mode');

export const trackNavModeToggled = (attributes: {
  from: NavMode;
  to: NavMode;
  forcedNavigation: boolean;
  fromPath: string;
}) => trackNavMode('Mode Toggled', attributes);

const trackSidebar = createTrackerForCategory('Sidebar');

export type SidebarToggleTrigger =
  | 'button'
  | 'keyboard'
  | 'resize-drag'
  | 'click-outside'
  | 'responsive'
  | 'navigation';

// `sidebar` identifies which shared sidebar emitted the event (e.g. 'project-entity', 'main-nav').
// Both share the useSidebarState primitive, so this keeps them distinguishable in Mixpanel even
// when they appear on the same page. `page` is the raw window.location.pathname (see proposal +
// review comments) so it can be regex-bucketed downstream (e.g. /projects/*/flags/*/targeting).
export const trackSidebarToggled = (attributes: {
  action: 'opened' | 'closed';
  trigger: SidebarToggleTrigger;
  page: string;
  sidebar?: string;
  width?: number;
}) => trackSidebar('Sidebar Toggled', attributes);

export const trackSidebarResized = (attributes: { width: number; page: string; sidebar?: string }) =>
  trackSidebar('Sidebar Resized', attributes);

export const trackSidebarSessionDuration = (attributes: { durationMs: number; page: string; sidebar?: string }) =>
  trackSidebar('Sidebar Session Duration', attributes);

const trackAgentHelp = createTrackerForCategory('Agent Help Button');

export type AgentHelpItem = 'discord' | 'product-docs' | 'contact-support';

export const trackAgentHelpItemClicked = (attributes: { item: AgentHelpItem }) =>
  trackAgentHelp('Item Clicked', attributes);
